import { useState, useRef, useEffect } from 'react'
import { emailTemplatesApi } from '../services/api'
import { Button, Badge } from './common'

const BatchRegenerateModal = ({ templateTypes, existingTemplates, onClose, onComplete }) => {
  const [step, setStep] = useState('configure')
  const [mode, setMode] = useState(0) // 0 = Redesign, 1 = Wipe
  const [tier, setTier] = useState(2)
  const [runParallel, setRunParallel] = useState(true)
  const [selectedTypes, setSelectedTypes] = useState([])
  const [selectAll, setSelectAll] = useState(false)
  const [progressEvents, setProgressEvents] = useState([])
  const [summary, setSummary] = useState(null)
  const abortRef = useRef(null)

  const existingTypeNames = new Set(
    (existingTemplates || []).map(t => {
      const type = templateTypes.find(tt => tt.value === parseInt(t.templateType))
      return type?.name || t.templateType?.toString()
    })
  )

  const handleSelectAll = (checked) => {
    setSelectAll(checked)
    setSelectedTypes(checked ? templateTypes.map(t => t.name) : [])
  }

  const handleToggleType = (name) => {
    setSelectedTypes(prev =>
      prev.includes(name)
        ? prev.filter(n => n !== name)
        : [...prev, name]
    )
  }

  useEffect(() => {
    setSelectAll(selectedTypes.length === templateTypes.length)
  }, [selectedTypes, templateTypes])

  const handleStart = async () => {
    setStep('progress')
    setProgressEvents([])
    setSummary(null)

    const controller = new AbortController()
    abortRef.current = controller

    // 10 min timeout for the entire batch (AI calls are slow)
    const timeoutId = setTimeout(() => controller.abort(), 600000)

    const token = localStorage.getItem('authToken')
    const url = emailTemplatesApi.getBatchStreamUrl()

    // Map selected type names to enum values
    const typeValues = selectedTypes.map(name => {
      const t = templateTypes.find(tt => tt.name === name)
      return t?.value
    }).filter(v => v !== undefined)

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          mode,
          templateTypes: typeValues,
          tier,
          runParallel
        }),
        signal: controller.signal
      })

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error')
        throw new Error(`Server returned ${response.status}: ${errorText}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        // Reset timeout on each chunk (server is still alive)
        clearTimeout(timeoutId)

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6))
              if (data.type === 'done' || data.summary) {
                setSummary(data.summary || data)
                setStep('summary')
              } else {
                setProgressEvents(prev => {
                  const idx = prev.findIndex(e => e.templateType === data.templateType)
                  if (idx >= 0) {
                    const next = [...prev]
                    next[idx] = data
                    return next
                  }
                  return [...prev, data]
                })
              }
            } catch {
              // skip invalid JSON
            }
          }
        }
      }
    } catch (err) {
      setStep('summary')
      setSummary(prev => prev || {
        total: selectedTypes.length,
        succeeded: progressEvents.filter(e => e.status === 'Complete').length,
        failed: progressEvents.filter(e => e.status === 'Error').length,
        cancelled: err.name === 'AbortError'
          ? selectedTypes.length - progressEvents.filter(e => e.status === 'Complete' || e.status === 'Error').length
          : 0,
        error: err.name !== 'AbortError' ? err.message : undefined
      })
    } finally {
      clearTimeout(timeoutId)
    }
  }

  const handleCancel = () => {
    if (abortRef.current) {
      abortRef.current.abort()
    }
  }

  const handleClose = () => {
    if (abortRef.current) {
      abortRef.current.abort()
    }
    if (onComplete) onComplete()
    onClose()
  }

  const completedCount = progressEvents.filter(e => e.status === 'Complete' || e.status === 'Error').length

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl mx-4" style={{ maxHeight: '80vh' }}>
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {step === 'configure' && 'Batch Generate Templates'}
            {step === 'progress' && `Generating Templates (${completedCount}/${selectedTypes.length})`}
            {step === 'summary' && 'Batch Complete'}
          </h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>

        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 140px)' }}>
          {/* Step 1: Configure */}
          {step === 'configure' && (
            <div className="space-y-6">
              {/* Mode Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Mode</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: 0, label: 'Redesign', desc: 'Restyle with fresh look, preserve content and variables' },
                    { value: 1, label: 'Wipe & Rebuild', desc: 'Generate completely new templates from scratch' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setMode(opt.value)}
                      className={`p-4 rounded-xl border-2 text-left transition-colors ${
                        mode === opt.value
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-sm font-semibold text-gray-900">{opt.label}</div>
                      <div className="text-xs text-gray-500 mt-1">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* AI Model */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">AI Model</label>
                <select
                  value={tier}
                  onChange={(e) => setTier(parseInt(e.target.value))}
                  className="input-field"
                >
                  <option value={1}>Tier 1 — Haiku (Fast)</option>
                  <option value={2}>Tier 2 — Default (Balanced)</option>
                  <option value={3}>Tier 3 — Sonnet (Best quality)</option>
                </select>
              </div>

              {/* Parallel */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={runParallel}
                  onChange={(e) => setRunParallel(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Run in parallel</span>
              </label>

              {/* Template Selection */}
              <div>
                <label className="flex items-center gap-3 mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Select All ({templateTypes.length})
                  </span>
                </label>
                <div className="max-h-52 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {templateTypes.map(type => (
                    <label key={type.name} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedTypes.includes(type.name)}
                        onChange={() => handleToggleType(type.name)}
                        className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 flex-1">{type.name}</span>
                      {!existingTypeNames.has(type.name) && (
                        <Badge variant="primary" size="small">new</Badge>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Progress */}
          {step === 'progress' && (
            <div className="space-y-2">
              {selectedTypes.map(typeName => {
                const event = progressEvents.find(e => e.templateType === typeName)
                return (
                  <div key={typeName} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                    {!event || event.status === 'Processing' ? (
                      <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                    ) : event.status === 'Complete' ? (
                      <span className="text-green-500 flex-shrink-0">&#10003;</span>
                    ) : (
                      <span className="text-red-500 flex-shrink-0">&#10005;</span>
                    )}
                    <span className="text-sm text-gray-700 flex-1">{typeName}</span>
                    {event?.status === 'Error' && (
                      <span className="text-xs text-red-500 max-w-48 truncate">{event.message}</span>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* Step 3: Summary */}
          {step === 'summary' && summary && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${summary.error ? 'bg-red-50 border border-red-200' : summary.failed > 0 || summary.cancelled > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                <p className="text-sm font-medium">
                  {summary.error ? 'Batch regeneration failed' : summary.cancelled > 0 ? 'Batch regeneration cancelled' : 'Batch regeneration complete'}
                </p>
                {summary.error && <p className="text-xs text-red-600 mt-1">{summary.error}</p>}
                {summary.duration && <p className="text-xs text-gray-500 mt-1">Duration: {summary.duration}</p>}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{summary.total}</div>
                  <div className="text-xs text-gray-500">Total</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{summary.succeeded}</div>
                  <div className="text-xs text-gray-500">Succeeded</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{summary.failed}</div>
                  <div className="text-xs text-gray-500">Failed</div>
                </div>
              </div>
              {summary.failed > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Failed Templates:</p>
                  <div className="space-y-1">
                    {progressEvents.filter(e => e.status === 'Error').map(e => (
                      <div key={e.templateType} className="text-sm p-2 bg-red-50 rounded border border-red-100">
                        <span className="font-medium">{e.templateType}</span>: <span className="text-red-600">{e.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          {step === 'configure' && (
            <>
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              <Button
                variant="primary"
                onClick={handleStart}
                disabled={selectedTypes.length === 0}
              >
                Start ({selectedTypes.length} templates)
              </Button>
            </>
          )}
          {step === 'progress' && (
            <Button variant="danger" onClick={handleCancel}>Cancel</Button>
          )}
          {step === 'summary' && (
            <Button variant="primary" onClick={handleClose}>Close</Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default BatchRegenerateModal
